import { getEnglishModule } from '@uhg-psx/mapping-files-translation-transperfect';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import translationsEnJson from '../../public/locale/transperfect/en/translation.json';
import {
  Constants,
  DEFAULT_SEARCH_RADIUS,
  EAP_OPTION_LABEL,
  FilterCollectionModel,
  FilterOptionValues,
  ReverseCoverageTypesCodes,
  VirtualCareOptions,
} from '../common/Constants';
import { ConstantsLagoon } from '../common/ConstantsLagoon';
import { staticAoeEnglishMap } from '../common/ContentFilters/AllFilterDrawer/ProviderFilters/GroupedAoeFilter/groupedAoe.utils';
import {
  getBestMatchOptionKey,
  getRecommendedProviderOptionKey,
} from '../common/ContentFilters/SortByUtil';
import { getIsVirtualCareV2 } from '../common/Utils/filters.utils';
import { getPlanSelectorVariables } from '../frontends/ProviderSearch/context/PlanSelector/helpers';
import {
  getAgesTreatedAoeCodes,
  getConditionAoeCodes,
} from '../frontends/ProviderSearch/routes/SearchResults/SearchResults.UtilsV2';
import { useCoverageType } from '../hooks/useCoverageType';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useLagoon } from '../hooks/useLagoon';
import { useSmartChoice } from '../hooks/useSmartChoice';
import {
  FiltersDefaultValues,
  ProviderSearchFilters,
} from '../models/ProviderSearch';
import { usePlanSelectorStore } from '../store/usePlanSelectorStore';
import { useTypeaheadStore } from '../store/useTypeaheadStore';
import { TypeaheadState } from '../store/useTypeaheadStore/typeaheadStore';

interface SearchFiltersContextType {
  searchFilters: ProviderSearchFilters;
  defaultSearchFilters: ProviderSearchFilters;
  setSearchFilters: (selectedFilters: ProviderSearchFilters) => void;
  setDefaultSearchFilters: (
    defaultFilters: FilterCollectionModel[],
    defaultValues?: FiltersDefaultValues,
    sectionType?: string,
    coverageTypeFromParams?: string
  ) => ProviderSearchFilters;
  clearSearchFilters: () => void;
  isSmartChoice: boolean;
  enableBHVirtualCareFilterV2: boolean;
}

type ProviderDefaultValue = {
  autoSearchRadius?: number;
};

export const SearchFilterContext = createContext<SearchFiltersContextType>({
  searchFilters: {},
  defaultSearchFilters: {},
  setSearchFilters: () => {},
  setDefaultSearchFilters: () => ({}),
  clearSearchFilters: () => {},
  isSmartChoice: false,
  enableBHVirtualCareFilterV2: false,
});

const hasMatchingAoeCode = (aoe, agesAoeCodes) =>
  agesAoeCodes?.includes(aoe.aoeCodes) ||
  aoe.aoeCodes?.split(',').filter((i) => agesAoeCodes?.includes(i)).length > 0;

const getFilterDefaultValues = (
  translate,
  requiredFilters: FilterCollectionModel[] = [],
  defaultValues: FiltersDefaultValues = {},
  coverageType: string,
  isSmartChoice: boolean,
  enableBHVirtualCareFilterV2: boolean,
  sectionType: string | undefined,
  defaultPlanTypeValue: any,
  enablePlanTypeSelectionInFilter: boolean
): ProviderSearchFilters => {
  const {
    preferredFacility,
    SpecialtyCode,
    AgesAreaOfExpertise,
    AreaOfExpertise,
    GenderCode,
    SortBy,
    Accreditation,
  } = defaultValues;
  const autoSearchRadius =
    defaultValues.autoSearchRadius ?? DEFAULT_SEARCH_RADIUS;
  const BEST_MATCH_SORT_LABEL_KEY = getBestMatchOptionKey(coverageType);
  const SMART_CHOICE_SORT_LABEL_KEY =
    getRecommendedProviderOptionKey(coverageType);

  const isVirtualCareV2 = getIsVirtualCareV2(
    enableBHVirtualCareFilterV2,
    coverageType,
    sectionType
  );
  const sortByArr = isSmartChoice
    ? {
        label: translate(`SORT_OPTIONS.${SMART_CHOICE_SORT_LABEL_KEY}`),
        value: Constants.RECOMMENDED_PROVIDERS_SEARCH_SORT_BY,
        hide: true,
        dnt_label: translationsEnJson.SORT_OPTIONS[SMART_CHOICE_SORT_LABEL_KEY],
      }
    : {
        label: translate(`SORT_OPTIONS.${BEST_MATCH_SORT_LABEL_KEY}`),
        value: Constants.PROVIDER_SEARCH_SORT_BY,
        hide: true,
        dnt_label: translationsEnJson.SORT_OPTIONS[BEST_MATCH_SORT_LABEL_KEY],
      };
  const filtersDefaultValues: ProviderSearchFilters = {
    [FilterCollectionModel.SORT_BY]: { ...sortByArr, ...SortBy },
    [FilterCollectionModel.ACCEPTING_NEW_PATIENTS]: [
      {
        label: translate('FILTER_OPTIONS.AcceptingNewPatients.Y'),
        value: FilterOptionValues.AcceptingNewPatients.Yes,
        dnt_label: translationsEnJson.FILTER_OPTIONS.AcceptingNewPatients.Y,
      },
    ],
    ...(isVirtualCareV2
      ? {
          [FilterCollectionModel.VIRTUAL_CARE_OPTIONS]: [
            {
              label: translate('FILTER_OPTIONS.VirtualCareOptions.VV'),
              value: VirtualCareOptions.OFFERS_VIRTUAL_VISITS,
              dnt_label:
                translationsEnJson.FILTER_OPTIONS.VirtualCareOptions.VV,
            },
          ],
        }
      : {
          [FilterCollectionModel.VIRTUAL_CARE]: {
            label: translate('FILTER_OPTIONS.VirtualCare.Y'),
            value: FilterOptionValues.VirtualCare.Yes,
            dnt_label: translationsEnJson.FILTER_OPTIONS.VirtualCare.Y,
          },
        }),
    [FilterCollectionModel.PREFERRED_FACILITY]: preferredFacility?.map(
      (value) => ({
        label: translationsEnJson.FILTER_OPTIONS.PreferredFacility[value],
        value,
        dnt_label: translationsEnJson.FILTER_OPTIONS.PreferredFacility[value],
      })
    ),
    [FilterCollectionModel.SPECIALTY_CODE]: SpecialtyCode?.map((value) => ({
      label: value.label,
      value: value.value,
      dnt_label: value.dnt_label,
    })),

    [FilterCollectionModel.AREA_OF_EXPERTISE]: AreaOfExpertise?.map(
      (value) => ({
        label: value.label,
        value: value.value,
        dnt_label: value.dnt_label,
      })
    ),

    [FilterCollectionModel.AGES_TREATED]: AgesAreaOfExpertise?.map((value) => ({
      label: value.label,
      value: value.value,
      dnt_label: value.dnt_label,
    })),

    [FilterCollectionModel.ACCREDITATION]: Accreditation?.map((value) => ({
      label: value.label,
      value: value.value,
      dnt_label: value.dnt_label,
    })),

    [FilterCollectionModel.GENDER_CODE]: GenderCode?.map((value) => ({
      label: value.label,
      value: value.value,
      dnt_label: value.dnt_label,
    })),
    [FilterCollectionModel.DISTANCE]: [
      {
        label: `${autoSearchRadius} ${translationsEnJson.miles}`,
        value: autoSearchRadius,
        dnt_label: `${autoSearchRadius} ${translationsEnJson.miles}`,
        c_value: autoSearchRadius,
        hide: !autoSearchRadius,
      },
    ],
    [FilterCollectionModel.AUTO_SEARCH_RADIUS]: autoSearchRadius,
    [FilterCollectionModel.ESSENTIAL_COMMUNITY_PROVIDER]: [
      {
        label: translate('FILTER_OPTIONS.EssentialCommunityProvider.Y'),
        value: FilterOptionValues.EssentialCommunityProvider.Yes,
        dnt_label:
          translationsEnJson.FILTER_OPTIONS.EssentialCommunityProvider.Y,
      },
    ],
    ...(enablePlanTypeSelectionInFilter &&
      defaultPlanTypeValue?.planName &&
      defaultPlanTypeValue?.coverageTypeReciprocityIdMap && {
        [FilterCollectionModel.PLAN_TYPE]: [
          {
            label: translate(defaultPlanTypeValue.planName),
            value:
              defaultPlanTypeValue?.coverageTypeReciprocityIdMap?.[
                coverageType
              ] || '',
            dnt_label: translate(defaultPlanTypeValue.planName),
          },
        ],
      }),
  };
  return requiredFilters.reduce((acc, key) => {
    if (filtersDefaultValues[key]) {
      acc[key] = filtersDefaultValues[key];
    }
    return acc;
  }, {});
};

export const SearchFilterContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const coverageType = useCoverageType();
  const { t } = useTranslation();
  const isSmartChoice = useSmartChoice();
  const [searchParams] = useSearchParams();
  const deepLinkMapping = useLagoon(Constants.LAGOON_TABLE.DEEPLINK_MAPPING)();
  const allAOEFilters = useLagoon(Constants.LAGOON_TABLE.AOE_FILTERS)();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { sectionType: sectionTypeFromStore } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      sectionType: state.typeaheadSearchStore.sectionType,
    }))
  );
  const planInfo = usePlanSelectorStore((state) => state.planInfo);
  const { planToken } = getPlanSelectorVariables();
  const defaultPlanTypeValue =
    Object.keys(planInfo).length > 1 ? planInfo?.[planToken] : {};

  const sectionType = searchParams.get('sectionType') || sectionTypeFromStore;
  const isDeepLink = searchParams.get('isDeepLink') == 'true';

  const [
    enableBHVirtualCareFilterV2,
    enablePlanTypeSelectionInFilter,
    enableBhGroupedAoeFilter,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_VIRTUAL_CARE_FILTER_V2,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLAN_TYPE_SELECTION_IN_FILTER,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_GROUPED_AOE_FILTER,
  ]);

  const [defaultValue, setDefaultvalue] =
    useSessionStorage<ProviderDefaultValue>(
      Constants.STORAGE_KEYS.SESSION.DEFAULT_SEARCH_FILTERS,
      {}
    );

  const defaultFiltersState: ProviderSearchFilters = useMemo(
    () =>
      getFilterDefaultValues(
        t,
        [
          FilterCollectionModel.AUTO_SEARCH_RADIUS,
          FilterCollectionModel.DISTANCE,
          FilterCollectionModel.PLAN_TYPE,
        ],
        defaultValue,
        coverageType,
        isSmartChoice,
        enableBHVirtualCareFilterV2,
        sectionType,
        defaultPlanTypeValue,
        enablePlanTypeSelectionInFilter
      ),
    [coverageType, JSON.stringify(defaultValue), isSmartChoice]
  );

  const [storedSearchFilters, setStoredSearchFilters] =
    useSessionStorage<ProviderSearchFilters>(
      Constants.STORAGE_KEYS.SESSION.SELECTED_SEARCH_FILTERS,
      defaultFiltersState
    );

  const skipNextFilterUpdateRef = useRef(false);
  const [searchFilters, setSearchFilters] = useState<ProviderSearchFilters>(
    isDeepLink ? defaultFiltersState : storedSearchFilters
  );
  const eapOptions = searchFilters?.AreaOfExpertise?.filter(
    (options) => options?.dnt_label === EAP_OPTION_LABEL
  );
  if (eapOptions && eapOptions.length > 0) {
    searchFilters.EapOffered = eapOptions;
  }

  useEffect(() => {
    const condition = searchParams.get('condition');

    // Process Ages Treated AOE codes
    const agesAoeCodes = condition
      ? getAgesTreatedAoeCodes(condition, deepLinkMapping)
      : [];
    let agesTreatedAOE: any = [];
    if (allAOEFilters) {
      agesTreatedAOE = allAOEFilters.filter((aoe) =>
        hasMatchingAoeCode(aoe, agesAoeCodes)
      );
    }

    // Create a new searchFilters object to avoid mutating state directly
    const updatedSearchFilters = { ...searchFilters };
    let hasUpdates = false;

    if (
      agesTreatedAOE &&
      searchFilters.AgesAreaOfExpertise === undefined &&
      isInitialLoad
    ) {
      updatedSearchFilters.AgesAreaOfExpertise = agesTreatedAOE.map((aoe) => ({
        label: t(aoe.dntName),
        value: aoe.aoeCodes,
        dnt_label: aoe.dntName,
      }));
      hasUpdates = true;
    }

    // Process regular Area of Expertise codes from condition parameter
    if (condition && deepLinkMapping && isInitialLoad) {
      let aoeTranslations = getEnglishModule('aoeTranslations');
      const isBhGroupedAoeCoverage =
        coverageType === ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'] ||
        coverageType === ReverseCoverageTypesCodes['BEHAVIORAL CARVE-OUT'];
      const isBhAoeGrouped = enableBhGroupedAoeFilter && isBhGroupedAoeCoverage;
      isBhAoeGrouped &&
        (aoeTranslations = { ...aoeTranslations, ...staticAoeEnglishMap });
      const { areaOfExpertise } = getConditionAoeCodes(
        condition,
        deepLinkMapping,
        aoeTranslations
      );

      if (areaOfExpertise?.length > 0 && !searchFilters.AreaOfExpertise) {
        updatedSearchFilters.AreaOfExpertise = areaOfExpertise;
        hasUpdates = true;
      }
    }

    if (isInitialLoad) {
      setIsInitialLoad(false);
    }

    // Only update state if there are actual changes
    if (hasUpdates) {
      setSearchFilters(updatedSearchFilters);
      setStoredSearchFilters(updatedSearchFilters);
    } else {
      setStoredSearchFilters(searchFilters);
    }
  }, [JSON.stringify(searchFilters)]);

  const onSearchFiltersChange = (selectedFilters: ProviderSearchFilters) => {
    setSearchFilters(selectedFilters);
  };
  useEffect(() => {
    if (skipNextFilterUpdateRef.current || isSmartChoice || isDeepLink) {
      skipNextFilterUpdateRef.current = false;
      return;
    }
    const updatedFilters = getFilterDefaultValues(
      t,
      [
        FilterCollectionModel.AUTO_SEARCH_RADIUS,
        FilterCollectionModel.DISTANCE,
      ],
      defaultValue,
      coverageType,
      isSmartChoice,
      enableBHVirtualCareFilterV2,
      sectionType,
      defaultPlanTypeValue,
      enablePlanTypeSelectionInFilter
    );

    setSearchFilters((prev) => ({ ...prev, ...updatedFilters }));
  }, [isSmartChoice]);

  const onDefaultFiltersChange = (
    defaultFilters: FilterCollectionModel[] = [],
    defaultValues: FiltersDefaultValues = {},
    sectionTypeFromParams?: string,
    coverageTypeFromParams?: string
  ) => {
    const filterValues = getFilterDefaultValues(
      t,
      [...defaultFilters, FilterCollectionModel.SORT_BY],
      defaultValues,
      coverageTypeFromParams ?? coverageType,
      isSmartChoice,
      enableBHVirtualCareFilterV2,
      sectionTypeFromParams || sectionType,
      defaultPlanTypeValue,
      enablePlanTypeSelectionInFilter
    );

    setDefaultvalue(defaultValues);
    let actualUpdatedFilters: ProviderSearchFilters | undefined;

    setSearchFilters((prev) => {
      const updatedFilters = {
        ...defaultFiltersState,
        ...prev,
        ...filterValues,
      } as ProviderSearchFilters;
      actualUpdatedFilters = updatedFilters;
      return updatedFilters;
    });

    return (
      actualUpdatedFilters ?? {
        ...defaultFiltersState,
        ...searchFilters,
        ...filterValues,
      }
    );
  };

  const onClearSearchFilters = () => {
    skipNextFilterUpdateRef.current = true;
    setSearchFilters(defaultFiltersState);
  };

  return (
    <SearchFilterContext.Provider
      value={{
        searchFilters,
        defaultSearchFilters: defaultFiltersState,
        setSearchFilters: onSearchFiltersChange,
        setDefaultSearchFilters: onDefaultFiltersChange,
        clearSearchFilters: onClearSearchFilters,
        isSmartChoice,
        enableBHVirtualCareFilterV2,
      }}
    >
      {children}
    </SearchFilterContext.Provider>
  );
};
